import React, { FC, useEffect, useLayoutEffect, useState } from "react";
import styles from "./navigationBar.module.scss";
import {
  Button,
  ClickAwayListener,
  createTheme,
  Grow,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  TextField,
} from "@mui/material";
import { Link } from "react-router-dom";
import { ROUTES } from "../../app/Routes";
import { ThemeProvider } from "@mui/styles";
import { PopupElement } from "../../ducks/popup";
import { IUpdateDocument, ITechReport } from "../../api/monitoringApi";
import { MonitoringStatuses } from "../../ducks/montitoring";
import { Icon } from "@iconify/react";
import { DiscrepancyFilters } from "./filters";
import { DocumentListLink } from "./document-list-link";
import { ActInfo } from "./act-info";
import swapIcon from "./swapIcon.png";
import acceptIcon from "./accept.svg";
import delayIcon from "./delayIcon.png";
import rejectIcon from "./reject.svg";
import reportIcon from "./report.svg";
import deleteIcon from "./delete.png";
import { NavigationBarIconButton } from "./filters/filters-styles";
import commentIcon from "./comment.png";

export interface NavigationBarStateProps {
  login: string;
  balance: number;
  header: string;
  isLoggedIn: boolean;
  docStatus: MonitoringStatuses;
  isDetailedPage: boolean;
  canGetBalance: boolean;
  activeAccountId: string;
  path: string;
  detailedDocLeftId: string;
  packageId: any;
  report: string;
  reportPackageId: number[];
  comment: string;
}

export interface NavigationBarDispatchProps {
  onLogout: () => void;
  getBalance: () => void;
  setUploadPopupOpen: (isOpen: boolean) => void;
  onGetDifferenceProtocol: () => void;
  onDownloadPdf: () => void;
  onSwapDocuments: () => void;
  popupPush: (popup: PopupElement) => void;
  updateDocumentStatus: (
    documentId: string,
    props: IUpdateDocument,
    helpForm?: boolean
  ) => void;
  routeTo: (route: string | null, id?: number) => void;
  sendTechnicalReport: (props: ITechReport) => void;
  updateTechReportText: (value: string) => void;
  deleteUnrecognizedPackagesImages: (packageID: number) => void;
}

type NavigationBarProps = NavigationBarStateProps & NavigationBarDispatchProps;

const theme = createTheme({});

export const NavigationBar: FC<NavigationBarProps> = ({
  onGetDifferenceProtocol,
  onDownloadPdf,
  onSwapDocuments,
  activeAccountId,
  docStatus,
  path,
  getBalance,
  canGetBalance,
  setUploadPopupOpen,
  balance,
  isDetailedPage,
  login,
  isLoggedIn,
  onLogout,
  popupPush,
  updateDocumentStatus,
  routeTo,
  sendTechnicalReport,
  updateTechReportText,
  deleteUnrecognizedPackagesImages,
  detailedDocLeftId,
  packageId,
  report,
  reportPackageId,
  comment,
}) => {
  const [openUserMenu, setOpenUserMenu] = useState(false);
  const disableGetDifferenceProtocolButton = [
    MonitoringStatuses.ACTS_PASSED,
    MonitoringStatuses.ACTS_MATCH,
  ].includes(docStatus);
  useEffect(() => {
    //@ts-ignore
    if (
      !activeAccountId &&
      !process.env.REACT_APP_TEST_TOKEN &&
      !process.env.REACT_APP_EXTERNAL_TOKEN_AUTH &&
      path !== ROUTES.Auth
    ) {
      window.location.href =
        process.env.REACT_APP_CORRECT_BASE_API +
        "/Integration/IntegrateWithReconciliationsActsService?redirectToDocshow=true";
    }
  }, [activeAccountId, path]);

  useLayoutEffect(() => {
    canGetBalance && getBalance();
  }, [canGetBalance, getBalance]);

  return (
    <header className={styles.container}>
      <div className={styles.leftContainer}>
        <div className={styles.iconContainer}>
          <Link to={"/"} className={styles.logo} />
          <div className={styles.title}>
            <div>Сравнение</div>
            <div>актов</div>
            <div>сверки</div>
          </div>
        </div>
        {isLoggedIn && isDetailedPage && <DocumentListLink />}
        {isLoggedIn && (
          <Button
            variant={"contained"}
            onClick={() => {
              setUploadPopupOpen(true);
            }}
            className={`${styles.link} ${styles.linkActive}`}
          >
            +&nbsp;&nbsp;Загрузить файл
          </Button>
        )}
        {isLoggedIn &&
          isDetailedPage &&
          ![
            MonitoringStatuses.WAITING_FOR_PAIR,
            MonitoringStatuses.ACTS_NOT_RECOGNISED,
          ].includes(docStatus) && <DiscrepancyFilters />}
        {isLoggedIn &&
          isDetailedPage &&
          ![
            MonitoringStatuses.WAITING_FOR_PAIR,
            MonitoringStatuses.ACTS_NOT_RECOGNISED,
          ].includes(docStatus) && (
            <NavigationBarIconButton onClick={() => onSwapDocuments()}>
              <img
                title={"Поменять документы местами"}
                src={swapIcon}
                alt="Поменять документы местами"
                width={24}
                height={24}
              />
            </NavigationBarIconButton>
          )}
        {isLoggedIn && isDetailedPage && (
          <div className={styles.actionButtonsContainer}>
            {docStatus !== MonitoringStatuses.ACTS_PASSED && (
              <>
                <NavigationBarIconButton
                  disabled={
                    docStatus === MonitoringStatuses.ACTS_NOT_RECOGNISED
                  }
                  onClick={() => {
                    popupPush({
                      actionTitle: "Принять",
                      actionVisible: true,
                      data: ["Вы действительно хотите принять акт?"],
                      actionHandler: () => {
                        updateDocumentStatus(detailedDocLeftId, {
                          comment,
                          status: 14,
                        });
                        routeTo("/");
                      },
                      cancelVisible: true,
                    });
                  }}
                >
                  <img
                    title={"Принять акт"}
                    src={acceptIcon}
                    alt={"Принять акт"}
                    width={24}
                    height={24}
                  />
                </NavigationBarIconButton>
                <NavigationBarIconButton
                  disabled={
                    docStatus === MonitoringStatuses.ACTS_NOT_RECOGNISED
                  }
                  title={"Акт отклонён"}
                  onClick={() => {
                    popupPush({
                      actionTitle: "Отклонить",
                      actionVisible: true,
                      data: ["Вы действительно хотите отклонить акт?"],
                      actionHandler: () => {
                        updateDocumentStatus(detailedDocLeftId, {
                          comment,
                          status: 15,
                        });
                        routeTo("/");
                      },
                      cancelVisible: true,
                    });
                  }}
                >
                  <img
                    title={"Отклонить акт"}
                    src={rejectIcon}
                    alt={"Отклонить акт"}
                    width={24}
                    height={24}
                  />
                </NavigationBarIconButton>
              </>
            )}
            <NavigationBarIconButton
              onClick={() => {
                popupPush({
                  technicalSupport: true,
                  actionTitle: "Отправить",
                  actionVisible: true,
                  data: [
                    "Вы действительно хотите отправить информацию об ошибке в техническую поддержку?",
                  ],
                  actionHandler: () => {
                    updateDocumentStatus(
                      detailedDocLeftId,
                      {
                        comment,
                        status: 13,
                      },
                      true
                    );
                    sendTechnicalReport({
                      message: report,
                      packageId: reportPackageId,
                      subject: "DocShow: Сверка актов сверки",
                    });
                    routeTo("/");
                  },
                  cancelHandler: () => {
                    updateTechReportText("");
                  },
                  cancelVisible: true,
                });
              }}
              title={"Помощь"}
            >
              <img
                title={"Сообщить об ошибке"}
                src={reportIcon}
                alt={"Сообщить об ошибке"}
                width={24}
                height={24}
              />
            </NavigationBarIconButton>
            <NavigationBarIconButton
              className={styles.materialButtonDelete}
              onClick={() => {
                popupPush({
                  actionTitle: "Удалить",
                  actionVisible: true,
                  data: ["Вы действительно хотите удалить данный акт?"],
                  actionHandler: () => {
                    if (docStatus === MonitoringStatuses.ACTS_NOT_RECOGNISED) {
                      deleteUnrecognizedPackagesImages(packageId);
                    } else {
                      updateDocumentStatus(detailedDocLeftId, {
                        comment,
                        status: MonitoringStatuses.DELETED,
                      });
                    }
                    routeTo("/");
                  },
                  cancelVisible: true,
                });
              }}
              title={"Удалить"}
            >
              <img
                title={"Удалить документ"}
                src={deleteIcon}
                alt={"Удалить документ"}
                width={24}
                height={24}
              />
            </NavigationBarIconButton>
            <NavigationBarIconButton
              onClick={() => {
                let newComment = comment || "";
                popupPush({
                  actionTitle: "Добавить комментарий",
                  actionVisible: true,
                  data: [
                    <div key="comment-input">
                      <TextField
                        sx={{
                          marginTop: "20px",
                          backgroundColor: "#FFF",
                          width: "425px",
                        }}
                        placeholder="Введите комментарий"
                        multiline
                        minRows={2}
                        maxRows={6}
                        variant="outlined"
                        defaultValue={newComment}
                        onChange={(e) => {
                          newComment = e.target.value;
                        }}
                        autoFocus
                      />
                    </div>,
                  ],

                  actionHandler: () => {
                    updateDocumentStatus(detailedDocLeftId, {
                      comment: newComment,
                      status: docStatus,
                    });
                    routeTo("/");
                  },
                  cancelVisible: true,
                  cancelHandler: () => {},
                });
              }}
              title={"Добавить комментарий"}
            >
              <img
                title={"Добавить комментарий"}
                src={commentIcon}
                alt={"Добавить комментарий"}
                width={24}
                height={24}
              />
            </NavigationBarIconButton>
          </div>
        )}
        {isLoggedIn &&
          isDetailedPage &&
          ![
            MonitoringStatuses.WAITING_FOR_PAIR,
            MonitoringStatuses.ACTS_NOT_RECOGNISED,
          ].includes(docStatus) && <ActInfo />}
        {isLoggedIn &&
          isDetailedPage &&
          ![
            MonitoringStatuses.WAITING_FOR_PAIR,
            MonitoringStatuses.ACTS_NOT_RECOGNISED,
          ].includes(docStatus) && (
            <Button
              disabled={disableGetDifferenceProtocolButton}
              variant={"contained"}
              onClick={() => {
                onGetDifferenceProtocol();
              }}
              className={`${styles.generateProtocol} ${
                disableGetDifferenceProtocolButton ? styles.disabled : ""
              }`}
            >
              <div>
                <Icon
                  style={{ objectFit: "contain" }}
                  icon="mdi:microsoft-excel"
                  color="#f57b20"
                  width="20"
                  height="20"
                />
              </div>
              <span>Сформировать акт разногласий</span>
            </Button>
          )}
        {isLoggedIn &&
          isDetailedPage &&
          process.env.REACT_APP_EXTERNAL_TOKEN_AUTH && (
            <Button
              variant={"contained"}
              onClick={() => {
                onDownloadPdf();
              }}
              className={`${styles.downloadCliche}`}
            >
              <div>
                <Icon
                  icon="carbon:generate-pdf"
                  color="#f57b20"
                  width="32"
                  height="22"
                />
              </div>
              <span>Скачать с клише</span>
            </Button>
          )}
      </div>
      <div className={styles.topPanelRight}>
        <div className={styles.row}>
          {isLoggedIn && !isDetailedPage && (
            <a
              style={{ textDecoration: "none" }}
              href={
                "https://docs.correct.su/docs-utils/%D0%A1%D1%80%D0%B0%D0%B2%D0%BD%D0%B5%D0%BD%D0%B8%D0%B5%20%D0%B0%D0%BA%D1%82%D0%BE%D0%B2%20%D1%81%D0%B2%D0%B5%D1%80%D0%BA%D0%B8"
              }
              target="_blank"
              rel="noreferrer"
            >
              <div className={styles.rowItem}>
                <div className={styles.infoIcon}></div>
                <div className={styles.textLink}>О сервисе</div>
              </div>
            </a>
          )}
          {!isLoggedIn && (
            <div className={styles.rowItem}>
              <a href={`${process.env.REACT_APP_CORRECT_LK_LOGIN_LINK}`}>
                <Button
                  variant={"contained"}
                  className={styles.buttonAction}
                  size={"small"}
                >
                  Войти
                </Button>
              </a>
              <a href={`${process.env.REACT_APP_CORRECT_LK_REGISTRATION_LINK}`}>
                <Button
                  variant={"contained"}
                  className={styles.buttonCancel}
                  size={"small"}
                >
                  Начать бесплатно
                </Button>
              </a>
            </div>
          )}
        </div>

        {isLoggedIn && (
          <div className={styles.itemColumn}>
            <div
              onClick={() => setOpenUserMenu(!openUserMenu)}
              className={styles.topPanelRightAvatarCont}
            >
              <span className={styles.topPanelRightAvatarUserName}>
                {login}
              </span>
              <span className={styles.arrowDown} />
            </div>
            <ThemeProvider theme={theme}>
              <Popper
                className={styles.topPanelRightAvatarMenu}
                open={openUserMenu}
                transition
                disablePortal
                style={{ top: 60, left: "auto", right: 50 }}
              >
                {({ TransitionProps, placement }) => (
                  <Grow
                    {...TransitionProps}
                    style={{
                      transformOrigin:
                        placement === "bottom" ? "center top" : "center bottom",
                    }}
                  >
                    <Paper>
                      <ClickAwayListener
                        onClickAway={() => setOpenUserMenu(false)}
                      >
                        <MenuList
                          id="menu-list-grow"
                          onKeyDown={() => setOpenUserMenu(!openUserMenu)}
                        >
                          <MenuItem onClick={() => setOpenUserMenu(false)}>
                            <a
                              rel={"noreferrer"}
                              target={"_blank"}
                              href={"https://lkk.correct.su/Billing/Tariffs"}
                            >
                              Личный кабинет
                            </a>
                          </MenuItem>
                          <MenuItem
                            onClick={() => {
                              onLogout();
                              setOpenUserMenu(false);
                            }}
                          >
                            Выход
                          </MenuItem>
                        </MenuList>
                      </ClickAwayListener>
                    </Paper>
                  </Grow>
                )}
              </Popper>
            </ThemeProvider>
            <div className={`${styles.row} ${styles.rowBalance}`}>
              {/*@ts-ignore*/}
              {!process.env.REACT_APP_EXTERNAL_TOKEN_AUTH && (
                <div className={styles.rowItem}>
                  <div className={styles.text}>Баланс:</div>
                  <div className={styles.textBalance}>{balance} страниц</div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </header>
  );
};
